
import { useEffect, useState, useCallback } from 'react';
import { getCurrentTenantId } from '@/lib/tenant';
import { API_URL } from '@/lib/api-url';

export interface TenantBranding {
  subdomain: string;
  businessName: string;
  primaryColor: string;
  logo: string | null;
}

const DEFAULT_PRIMARY_COLOR = '#C0392B';
const BRANDING_STYLE_ID = 'tenant-branding-styles';
const BRANDING_API_BASE = API_URL;
const BRANDING_ASSET_ORIGIN = (() => {
  try {
    return new URL(BRANDING_API_BASE).origin;
  } catch {
    return '';
  }
})();

function getBrandingTenantCandidates(tenantId: string): string[] {
  const normalized = tenantId.trim().toLowerCase();
  if (!normalized) {
    return [];
  }

  const candidates = new Set<string>([normalized]);

  // Fallback aliases for known tenant key variations
  if (normalized === 'flex') {
    candidates.add('flexgym');
  }
  if (normalized === 'flexgym') {
    candidates.add('flex');
  }

  return Array.from(candidates);
}

function normalizeLogoUrl(logo: string | null): string | null {
  if (!logo) {
    return null;
  }

  const trimmed = logo.trim();
  if (!trimmed) {
    return null;
  }

  if (/^https?:\/\//i.test(trimmed) || /^data:/i.test(trimmed)) {
    return trimmed;
  }

  if (!BRANDING_ASSET_ORIGIN) {
    return trimmed;
  }

  if (trimmed.startsWith('/')) {
    return `${BRANDING_ASSET_ORIGIN}${trimmed}`;
  }

  return `${BRANDING_ASSET_ORIGIN}/${trimmed}`;
}

/**
 * Convert hex color to HSL values for CSS custom properties
 */
function hexToHSL(hex: string): { h: number; s: number; l: number } | null {
  // Remove the # if present
  hex = hex.replace(/^#/, '');

  // Parse the hex values
  let r: number, g: number, b: number;
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16) / 255;
    g = parseInt(hex[1] + hex[1], 16) / 255;
    b = parseInt(hex[2] + hex[2], 16) / 255;
  } else if (hex.length === 6) {
    r = parseInt(hex.substring(0, 2), 16) / 255;
    g = parseInt(hex.substring(2, 4), 16) / 255;
    b = parseInt(hex.substring(4, 6), 16) / 255;
  } else {
    return null;
  }

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Apply branding colors by injecting a style tag
 * This approach ensures higher CSS specificity than theme classes
 */
function applyBrandingColors(primaryColor: string) {
  const hsl = hexToHSL(primaryColor);
  if (!hsl) {
    console.error('[Branding] Failed to parse color:', primaryColor);
    return;
  }

  console.log('[Branding] Applying color:', primaryColor, '-> HSL:', hsl);

  const hslValue = `${hsl.h} ${hsl.s}% ${hsl.l}%`;

  // Calculate a contrasting foreground color
  const foregroundL = hsl.l > 50 ? 10 : 98;
  const foregroundValue = `0 0% ${foregroundL}%`;

  console.log('[Branding] Setting --primary to:', hslValue);

  // Remove existing branding styles if any
  const existingStyle = document.getElementById(BRANDING_STYLE_ID);
  if (existingStyle) {
    existingStyle.remove();
  }

  // Create a new style element with high specificity CSS
  const style = document.createElement('style');
  style.id = BRANDING_STYLE_ID;
  style.textContent = `
    /* Override all theme variants with maximum specificity */
    :root,
    html,
    html.light,
    html.dark,
    :root.light,
    :root.dark,
    .light,
    .dark,
    [data-theme="light"],
    [data-theme="dark"] {
      --primary: ${hslValue} !important;
      --primary-foreground: ${foregroundValue} !important;
      --ring: ${hslValue} !important;
      --sidebar-primary: ${hslValue} !important;
      --sidebar-primary-foreground: ${foregroundValue} !important;
      --sidebar-ring: ${hslValue} !important;
    }
  `;

  document.head.appendChild(style);
  console.log('[Branding] Injected branding style tag');
}

/**
 * Remove branding styles
 */
function removeBrandingStyles() {
  const existingStyle = document.getElementById(BRANDING_STYLE_ID);
  if (existingStyle) {
    existingStyle.remove();
  }
}

/**
 * Hook to fetch and apply tenant branding based on subdomain
 */
export function useTenantBranding() {
  const [branding, setBranding] = useState<TenantBranding | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBranding = useCallback(async () => {
    // Get tenant ID from subdomain or localStorage
    const tenantId = getCurrentTenantId();
    console.log('[Branding] Tenant ID detected:', tenantId);

    // Skip if no tenant
    if (!tenantId) {
      console.log('[Branding] No tenant ID, skipping');
      setLoading(false);
      return;
    }

    try {
      const candidates = getBrandingTenantCandidates(tenantId);
      console.log('[Branding] Fetching branding candidates:', candidates);

      let data: TenantBranding | null = null;
      let firstSuccessfulBranding: TenantBranding | null = null;
      for (const candidate of candidates) {
        const response = await fetch(`${BRANDING_API_BASE}/tenants/branding/${candidate}`);
        if (response.ok) {
          const candidateData: TenantBranding = await response.json();
          if (!firstSuccessfulBranding) {
            firstSuccessfulBranding = candidateData;
          }
          // Prefer candidate that has a valid logo set
          if (normalizeLogoUrl(candidateData.logo)) {
            data = candidateData;
            break;
          }
          continue;
        }

        // Stop early for non-404 errors
        if (response.status !== 404) {
          throw new Error('Failed to load branding');
        }
      }

      if (!data && firstSuccessfulBranding) {
        data = firstSuccessfulBranding;
      }

      if (!data) {
        // Tenant branding not found, use defaults
        setLoading(false);
        return;
      }

      data = {
        ...data,
        logo: normalizeLogoUrl(data.logo),
      };

      console.log('[Branding] Received data:', data);
      setBranding(data);

      // Apply CSS custom properties for the primary color
      if (data.primaryColor) {
        console.log('[Branding] About to apply color:', data.primaryColor);
        applyBrandingColors(data.primaryColor);
      } else {
        console.log('[Branding] No primaryColor in response');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load branding');
      console.error('Error loading tenant branding:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  const refetch = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchBranding();
  }, [fetchBranding]);

  return { branding, loading, error, refetch };
}

/**
 * Utility to manually apply branding colors (useful for previewing in signup)
 */
export function previewBrandingColor(color: string) {
  applyBrandingColors(color);
}

/**
 * Reset branding colors to defaults (removes injected styles)
 */
export function resetBrandingColors() {
  removeBrandingStyles();
}

export { DEFAULT_PRIMARY_COLOR };
